import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Box, Button, Container, Paper, Typography, Alert} from '@mui/material';
import {DateTime} from 'luxon';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import {getPlayerSessionDetailAsync} from '../../../store/playerSessionSlice';
import {updateSessionVideoSegmentOffset} from '../../../api/playerSessionAPI';
import {
  matchShiftToVideoSegment,
  groupSegmentsIntoChunks,
} from '../../../services/videoService';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import PageTitle from '../PageTitle';
import BackButton from '../BackButton';
import PlayerSessionShiftSlider from '../../PlayerSessions/PlayerSessionShiftSlider';
import ShiftTimeAdjustmentSlider from './ShiftTimeAdjustmentSlider';
import {useTranslation} from 'react-i18next';
import {Roles, SessionTypes} from '../../../enums';
import {
  CLIP_START_OFFSET,
  GAME_CLIP_END_OFFSET,
  PRACTICE_CLIP_END_OFFSET,
  SHIFT_VIDEO_ADJUSTMENT_RANGE_SECONDS,
} from '../../../constants';
import {Helmet} from 'react-helmet';
import FullScreenLoadingSpinner from '../FullScreenLoading';

interface Props {}

const AlignShiftVideo: React.FC<Props> = () => {
  const {teamId, teamSessionId, profileId, playerSessionId} = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const playerSession = useAppSelector(
    state => state.playerSession?.playerSession,
  );
  const {t} = useTranslation();
  const playerSessionStatus = useAppSelector(
    state => state.playerSession?.status,
  );
  const role = useAppSelector(state => state.profile?.profile?.role);

  // Team sessions can only be aligned by coaches or admins. Guard the page
  // itself (not just the entry button) so non-privileged users can't reach
  // this route directly and call updateSessionVideoSegmentOffset.
  const requiresCoachOrAdmin = useMemo(
    () =>
      !!playerSession?.teamSession &&
      ![Roles.coach, Roles.admin].includes(role),
    [playerSession?.teamSession, role],
  );
  const [videoIndex, setVideoIndex] = useState(0);
  // Offsets keyed by chunk ID (first segment ID in the chunk)
  const [offsets, setOffsets] = useState<Record<number, number>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');

  const videoRef = useRef<HTMLVideoElement>(null);

  const playerSessionIdInt = parseInt(playerSessionId ?? '');

  // set the back url
  const backUrl = useMemo(() => {
    if (teamId && teamSessionId && playerSessionId) {
      return `/teams/${teamId}/teamsessions/${teamSessionId}/playersessions/${playerSessionId}`;
    } else if (teamId && profileId && playerSessionId) {
      return `/teams/${teamId}/players/${profileId}/playersessions/${playerSessionId}`;
    } else if (profileId && playerSessionId) {
      return `/family/children/${profileId}/playersessions/${playerSessionId}`;
    } else if (playerSessionId) {
      return `/playersessions/${playerSessionId}`;
    } else {
      return '/';
    }
  }, [playerSessionId, profileId, teamId, teamSessionId]);

  const sessionVideoSegments = useMemo(
    () =>
      playerSession?.sessionVideos?.[0]?.sessionVideoSegments ??
      playerSession?.teamSession?.sessionVideos?.[0]?.sessionVideoSegments ??
      [],
    [playerSession?.sessionVideos, playerSession?.teamSession?.sessionVideos],
  );

  // Group contiguous video segments into chunks
  const sessionVideoChunks = useMemo(
    () => groupSegmentsIntoChunks(sessionVideoSegments),
    [sessionVideoSegments],
  );

  // Mark shifts as disabled if they don't have a matching video segment
  const shiftsWithDisabled = useMemo(() => {
    if (!playerSession?.playerSessionShifts) return [];
    return playerSession.playerSessionShifts
      .filter(pss => pss.startTime && pss.endTime && pss.valid)
      .map(pss => {
        const matched = matchShiftToVideoSegment({
          sessionVideoSegments,
          playerSessionShift: pss,
          adjustmentRange: SHIFT_VIDEO_ADJUSTMENT_RANGE_SECONDS,
          sessionType: playerSession?.type as SessionTypes,
        });
        return {...pss, disabled: !matched};
      });
  }, [
    playerSession?.playerSessionShifts,
    playerSession?.type,
    sessionVideoSegments,
  ]);

  const currentShift = shiftsWithDisabled[videoIndex];

  const shift = useMemo(() => {
    if (!currentShift?.startTime || !currentShift?.endTime) {
      return null;
    }

    const shiftStartTime = DateTime.fromISO(currentShift.startTime).plus({
      seconds: CLIP_START_OFFSET,
    });
    const shiftEndTime = DateTime.fromISO(currentShift.endTime).plus({
      seconds:
        playerSession?.type === SessionTypes.game
          ? GAME_CLIP_END_OFFSET
          : PRACTICE_CLIP_END_OFFSET,
    });
    return {
      ...currentShift,
      startTime: shiftStartTime.toISO() ?? currentShift.startTime,
      endTime: shiftEndTime.toISO() ?? currentShift.endTime,
    };
  }, [currentShift, playerSession?.type]);

  const sessionVideoSegment = useMemo(() => {
    if (!currentShift) {
      return null;
    }
    // Pass the raw shift — matchShiftToVideoSegment applies clip offsets internally
    return matchShiftToVideoSegment({
      sessionVideoSegments,
      playerSessionShift: currentShift,
      adjustmentRange: SHIFT_VIDEO_ADJUSTMENT_RANGE_SECONDS,
      sessionType: playerSession?.type as SessionTypes,
    });
  }, [playerSession?.type, sessionVideoSegments, currentShift]);

  // Find the chunk that contains the current segment
  const currentChunk = useMemo(() => {
    if (!sessionVideoSegment) return null;
    return (
      sessionVideoChunks.find(chunk =>
        chunk.some(seg => seg.id === sessionVideoSegment.id),
      ) ?? null
    );
  }, [sessionVideoChunks, sessionVideoSegment]);

  // Stable chunk identifier (first segment id in the chunk)
  const currentChunkId = currentChunk?.[0]?.id ?? null;

  // Current offset for the active chunk
  const offset = currentChunkId != null ? offsets[currentChunkId] ?? 0 : 0;

  // Calculate adjusted times
  const adjustedShiftStart = useMemo(() => {
    if (!shift?.startTime) return null;
    return DateTime.fromISO(shift.startTime).plus({seconds: offset});
  }, [shift?.startTime, offset]);

  const adjustedShiftEnd = useMemo(() => {
    if (!shift?.endTime) return null;
    return DateTime.fromISO(shift.endTime).plus({seconds: offset});
  }, [shift?.endTime, offset]);

  // fetch the session
  useEffect(() => {
    dispatch(getPlayerSessionDetailAsync(playerSessionIdInt));
  }, [dispatch, playerSessionIdInt]);

  // On load, advance to the first alignable shift
  useEffect(() => {
    const firstAlignableIndex = shiftsWithDisabled.findIndex(s => !s.disabled);
    if (firstAlignableIndex > 0) {
      setVideoIndex(firstAlignableIndex);
    }
  }, [shiftsWithDisabled]);

  // Initialize offset for the current chunk from userSelectedOffset if not already set
  useEffect(() => {
    if (currentChunkId == null) return;
    setOffsets(prev => {
      if (prev[currentChunkId] !== undefined) return prev;
      return {
        ...prev,
        [currentChunkId]: sessionVideoSegment?.userSelectedOffset ?? 0,
      };
    });
  }, [currentChunkId, sessionVideoSegment?.userSelectedOffset]);

  // When switching shifts on the same video, seek to the new shift start
  const prevVideoIndexRef = useRef(videoIndex);
  useEffect(() => {
    if (prevVideoIndexRef.current === videoIndex) return;
    prevVideoIndexRef.current = videoIndex;

    const video = videoRef.current;
    if (!video || !adjustedShiftStart || !sessionVideoSegment) return;

    const segmentStart = DateTime.fromISO(
      sessionVideoSegment?.userSelectedStartTime ??
        sessionVideoSegment?.startTime ??
        '',
    );
    const seekTarget = adjustedShiftStart.diff(segmentStart, 'seconds').seconds;

    if (video.readyState >= 1) {
      video.currentTime = Math.max(0, seekTarget);
    }
  }, [videoIndex, adjustedShiftStart, sessionVideoSegment]);

  const handleShiftOffsetChange = (newOffset: number) => {
    if (currentChunkId == null) return;
    setOffsets(prev => ({...prev, [currentChunkId]: newOffset}));
    setHasChanges(true);
    setSaveStatus('idle');
  };

  const handleSave = async () => {
    if (requiresCoachOrAdmin) return;
    setSaveStatus('saving');
    try {
      // Build offset pairs for all chunks that have offsets
      const segmentOffsetPairs = sessionVideoChunks.flatMap(chunk => {
        const chunkId = chunk[0]?.id;
        if (chunkId == null || offsets[chunkId] === undefined) return [];
        return chunk
          .filter(seg => seg.id)
          .map(seg => ({
            sessionVideoSegmentId: seg.id!,
            offset: offsets[chunkId],
          }));
      });

      if (segmentOffsetPairs.length === 0) {
        setSaveStatus('error');
        return;
      }

      await updateSessionVideoSegmentOffset({
        sessionVideoSegments: segmentOffsetPairs,
      });

      setSaveStatus('saved');
      setHasChanges(false);
      navigate(backUrl);
    } catch (error) {
      console.error('Error saving shift adjustments:', error);
      setSaveStatus('error');
    }
  };

  const handleReset = () => {
    // Reset all chunk offsets to their original userSelectedOffset values
    const resetOffsets: Record<number, number> = {};
    sessionVideoChunks.forEach(chunk => {
      const chunkId = chunk[0]?.id;
      if (chunkId != null) {
        resetOffsets[chunkId] = chunk[0]?.userSelectedOffset ?? 0;
      }
    });
    setOffsets(resetOffsets);
    setHasChanges(false);
    setSaveStatus('idle');
  };

  const renderHeader = useCallback(
    () => (
      <>
        <Helmet>
          <title>{t('shiftAdjustment.alignShiftVideo')}</title>
        </Helmet>
        <PageTitle>{t('shiftAdjustment.alignShiftVideo')}</PageTitle>
        <BackButton navigationUrl={backUrl}>
          {t('navigation.back', {pageName: t('pages.playerSessionDetail')})}
        </BackButton>
      </>
    ),
    [t, backUrl],
  );

  if (playerSessionStatus === 'loading' || !playerSession) {
    return (
      <Container>
        {renderHeader()}
        <FullScreenLoadingSpinner />
      </Container>
    );
  } else if (requiresCoachOrAdmin) {
    return (
      <Container>
        {renderHeader()}
        <Alert severity="info">
          {t('shiftAdjustment.unableToEditShiftVideoForTeamSession')}
        </Alert>
      </Container>
    );
  } else if (shiftsWithDisabled.length === 0) {
    return (
      <Container>
        {renderHeader()}
        <Alert severity="error">
          {t('shiftAdjustment.noVideoSegmentFound')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      {renderHeader()}
      <Typography variant="h4">
        {t('shiftAdjustment.alignShiftVideoInstructionsTitle')}
      </Typography>
      <ul>
        {(
          t('shiftAdjustment.instructions', {
            returnObjects: true,
          }) as string[]
        ).map(instruction => (
          <li key={instruction}>
            <Typography variant="body1">{instruction}</Typography>
          </li>
        ))}
      </ul>

      {/* Shift selector slider */}
      <Box sx={{mb: 2}}>
        <PlayerSessionShiftSlider
          sessionType={playerSession?.type}
          shiftClips={shiftsWithDisabled}
          videoIndex={videoIndex}
          setVideoIndex={setVideoIndex}
          videoRef={videoRef}
          solidButtons
        />
      </Box>

      {shift && sessionVideoSegment ? (
        <Paper sx={{p: 3, mb: 3}}>
          <VideoPlayer
            videoRef={videoRef}
            sourceUrl={sessionVideoSegment?.videoUrl}
            sessionType={playerSession?.type}
            shiftClips={[]}
            videoIndex={0}
            setVideoIndex={() => {}}
            drillStartTime={adjustedShiftStart?.toJSDate()}
            drillEndTime={adjustedShiftEnd?.toJSDate()}
            videoSegmentStartTime={DateTime.fromISO(
              sessionVideoSegment?.userSelectedStartTime ??
                sessionVideoSegment?.startTime ??
                '',
            ).toJSDate()}
            hideSeekbar={true}
          />
          {/* Custom Dual-Handle Slider */}
          <ShiftTimeAdjustmentSlider
            videoRef={videoRef}
            shiftStartTime={shift?.startTime ?? ''}
            shiftEndTime={shift?.endTime ?? ''}
            videoSegmentStartTime={
              sessionVideoSegment?.userSelectedStartTime ??
              sessionVideoSegment?.startTime ??
              ''
            }
            videoSegmentDuration={sessionVideoSegment?.duration ?? 0}
            shiftOffset={offset}
            onShiftOffsetChange={handleShiftOffsetChange}
            adjustmentRange={SHIFT_VIDEO_ADJUSTMENT_RANGE_SECONDS}
          />

          <Alert severity="info" variant="outlined" sx={{my: 4}}>
            {t('shiftAdjustment.alignShiftVideoReminder')}
          </Alert>

          {/* Action Buttons */}
          <Box
            sx={{pt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end'}}>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={!hasChanges || saveStatus === 'saving'}>
              {t('shiftAdjustment.reset')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!hasChanges || saveStatus === 'saving'}>
              {saveStatus === 'saving'
                ? t('shiftAdjustment.saving')
                : t('shiftAdjustment.saveAdjustments')}
            </Button>
          </Box>

          {/* Save Status */}
          {saveStatus === 'saved' && (
            <Alert severity="success" sx={{mt: 2}}>
              {t('shiftAdjustment.saved')}
            </Alert>
          )}
          {saveStatus === 'error' && (
            <Alert severity="error" sx={{mt: 2}}>
              {t('shiftAdjustment.error')}
            </Alert>
          )}
        </Paper>
      ) : (
        <Alert severity="info">
          {t('shiftAdjustment.noVideoSegmentFound')}
        </Alert>
      )}
    </Container>
  );
};

export default AlignShiftVideo;
